// Editing model for the daily routes editor. Every function returns a new
// draft and never mutates its input.
//
// An address belongs to a code on one route: the same code can mean another
// shop on another route ("MOTO-FAMILIA" in Jelcz on D32, in Oława on DR2).
import { compact, norm } from "./names"
import type { RouteStopOrder, RoutesData } from "./types"

// a code on one route
export type StopRef = { route: string; code: string }

export type PendingAddress = StopRef & { address: string; changeId: number }

export type DailyRoutesDraft = {
  // last read or written state
  base: RoutesData
  // full, edited copy of base.routes
  orders: RouteStopOrder
  // stopKey(route, code) -> new address (new codes too)
  pending: Record<string, PendingAddress>
  nextChangeId: number
}

export type RouteChanges = {
  route: string
  order: string[]
  // false when the order is the one read last: saving must then leave the
  // stored order alone, as it may have been changed elsewhere since
  orderChanged: boolean
  // may reach other routes too: one "change all" is saved as a whole
  addresses: (StopRef & { address: string })[]
}

export type NewStopCheck =
  | { ok: true; existing: string | null }
  | { ok: false; reason: "empty" | "on-route" | "collides"; existing?: string }

const has = (record: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key)

// route codes and stop codes never hold a NUL
export const stopKey = (route: string, code: string): string =>
  `${route}\u0000${code}`

const copyRoutes = (routes: RouteStopOrder): RouteStopOrder =>
  Object.fromEntries(
    Object.entries(routes).map(([route, codes]) => [route, [...codes]]),
  )

const sameOrder = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((code, i) => code === b[i])

export const createDraft = (base: RoutesData): DailyRoutesDraft => ({
  base,
  orders: copyRoutes(base.routes),
  pending: {},
  nextChangeId: 0,
})

// The route's own address, else the one shared by every route; the wizard
// looks names up in the same order.
export const storedAddress = (
  data: RoutesData,
  route: string,
  code: string,
): string | undefined => {
  const own = has(data.routeAliases, route)
    ? data.routeAliases[route]
    : undefined
  if (own && has(own, code)) return own[code].address
  return has(data.aliases, code) ? data.aliases[code].address : undefined
}

export const currentAddress = (
  d: DailyRoutesDraft,
  route: string,
  code: string,
): string | undefined => {
  const key = stopKey(route, code)
  if (has(d.pending, key)) return d.pending[key].address
  return storedAddress(d.base, route, code)
}

// An address to start from for a code that has none on this route yet
export const suggestedAddress = (
  d: DailyRoutesDraft,
  route: string,
  code: string,
): string | undefined => {
  const own = currentAddress(d, route, code)
  if (own !== undefined) return own
  const elsewhere = Object.values(d.pending).find((p) => p.code === code)
  if (elsewhere) return elsewhere.address
  const stored = Object.values(d.base.routeAliases).find((aliases) =>
    has(aliases, code),
  )
  return stored?.[code].address
}

export const otherRoutes = (
  d: DailyRoutesDraft,
  code: string,
  route: string,
): string[] =>
  Object.keys(d.orders).filter(
    (other) => other !== route && d.orders[other].includes(code),
  )

// every code known to the draft: the shared catalogue first, then codes
// only some route or a pending edit has
export const knownCodes = (d: DailyRoutesDraft): string[] => [
  ...new Set([
    ...Object.keys(d.base.aliases),
    ...Object.values(d.base.routeAliases).flatMap(Object.keys),
    ...Object.values(d.pending).map((p) => p.code),
  ]),
]

const isKnown = (d: DailyRoutesDraft, code: string): boolean =>
  knownCodes(d).includes(code)

// Stops on any route that go to the same address as this one does now
export const sameAddressStops = (
  d: DailyRoutesDraft,
  route: string,
  code: string,
): StopRef[] => {
  // the wizard merges stops by norm(address), so the group does too
  const address = norm(currentAddress(d, route, code) ?? "")
  if (!address) return []
  const seen = new Set([stopKey(route, code)])
  const stops: StopRef[] = []
  for (const [other, codes] of Object.entries(d.orders)) {
    for (const otherCode of codes) {
      const key = stopKey(other, otherCode)
      if (seen.has(key)) continue
      seen.add(key)
      if (norm(currentAddress(d, other, otherCode) ?? "") === address) {
        stops.push({ route: other, code: otherCode })
      }
    }
  }
  return stops
}

// A code that already exists is used exactly as stored; only a new code is
// trimmed. Trimming a stored "GRANEX " would create a second alias that the
// wizard cannot tell from the first.
const resolveCode = (d: DailyRoutesDraft, code: string): string =>
  isKnown(d, code) ? code : code.trim()

export const setAddress = (
  d: DailyRoutesDraft,
  stops: StopRef[],
  address: string,
): DailyRoutesDraft => {
  const trimmed = address.trim()
  if (!trimmed) throw new Error("Address must not be empty")
  const pending = { ...d.pending }
  for (const { route, code } of stops) {
    const resolved = resolveCode(d, code)
    pending[stopKey(route, resolved)] = {
      route,
      code: resolved,
      address: trimmed,
      changeId: d.nextChangeId,
    }
  }
  return { ...d, pending, nextChangeId: d.nextChangeId + 1 }
}

const withRoute = (
  d: DailyRoutesDraft,
  route: string,
  codes: string[],
): DailyRoutesDraft => ({ ...d, orders: { ...d.orders, [route]: codes } })

export const moveStop = (
  d: DailyRoutesDraft,
  route: string,
  from: number,
  to: number,
): DailyRoutesDraft => {
  const codes = d.orders[route] ?? []
  const inRange = (i: number) =>
    Number.isInteger(i) && i >= 0 && i < codes.length
  if (!inRange(from) || !inRange(to) || from === to) return d
  const next = [...codes]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return withRoute(d, route, next)
}

export const removeStop = (
  d: DailyRoutesDraft,
  route: string,
  index: number,
): DailyRoutesDraft => {
  const codes = d.orders[route] ?? []
  if (!Number.isInteger(index) || index < 0 || index >= codes.length) return d
  return withRoute(
    d,
    route,
    codes.filter((_, i) => i !== index),
  )
}

export const addStop = (
  d: DailyRoutesDraft,
  route: string,
  code: string,
  address: string | null,
): DailyRoutesDraft => {
  const resolved = resolveCode(d, code)
  if (address === null && currentAddress(d, route, resolved) === undefined) {
    throw new Error(`Code ${resolved} has no address on ${route}`)
  }
  const added = withRoute(d, route, [...(d.orders[route] ?? []), resolved])
  return address === null
    ? added
    : setAddress(added, [{ route, code: resolved }], address)
}

// A code renamed in place is checked like a new one, apart from its own row
// The stop keeps its place and its address on the route under the new code;
// the same code on other routes is left alone.
export const renameStop = (
  d: DailyRoutesDraft,
  route: string,
  index: number,
  code: string,
): DailyRoutesDraft => {
  const codes = d.orders[route] ?? []
  if (!Number.isInteger(index) || index < 0 || index >= codes.length) return d
  const old = codes[index]
  const renamed = resolveCode(d, code)
  if (!renamed || renamed === old) return d
  const address = currentAddress(d, route, old)
  const next = codes.map((c, i) => (i === index ? renamed : c))
  const pending = { ...d.pending }
  // an edit of the old code only follows it while it is still on the route
  if (!next.includes(old)) delete pending[stopKey(route, old)]
  const moved = { ...withRoute(d, route, next), pending }
  if (
    address === undefined ||
    currentAddress(moved, route, renamed) === address
  )
    return moved
  return setAddress(moved, [{ route, code: renamed }], address)
}

export const checkNewStop = (
  d: DailyRoutesDraft,
  route: string,
  code: string,
  renamedIndex?: number,
): NewStopCheck => {
  const trimmed = code.trim()
  if (!trimmed) return { ok: false, reason: "empty" }
  const normalized = norm(trimmed)
  const onRoute = (d.orders[route] ?? []).some(
    (other, i) => i !== renamedIndex && norm(other) === normalized,
  )
  if (onRoute) {
    return { ok: false, reason: "on-route" }
  }
  if (isKnown(d, trimmed)) {
    return { ok: true, existing: trimmed }
  }
  const squashed = compact(trimmed)
  const collision = knownCodes(d).find(
    (other) =>
      norm(other) === normalized ||
      (squashed !== "" && compact(other) === squashed),
  )
  if (collision !== undefined) {
    return { ok: false, reason: "collides", existing: collision }
  }
  return { ok: true, existing: null }
}

// changeIds of the pending edits made on this route
const routeChangeIds = (d: DailyRoutesDraft, route: string): Set<number> =>
  new Set(
    Object.values(d.pending)
      .filter((entry) => entry.route === route)
      .map((entry) => entry.changeId),
  )

const pendingWithIds = (
  d: DailyRoutesDraft,
  ids: Set<number>,
): [string, PendingAddress][] =>
  Object.entries(d.pending).filter(([, entry]) => ids.has(entry.changeId))

export const isRouteDirty = (d: DailyRoutesDraft, route: string): boolean =>
  !sameOrder(d.orders[route] ?? [], d.base.routes[route] ?? []) ||
  Object.values(d.pending).some((entry) => entry.route === route)

export const changesForRoute = (
  d: DailyRoutesDraft,
  route: string,
): RouteChanges => ({
  route,
  order: [...(d.orders[route] ?? [])],
  orderChanged: !sameOrder(d.orders[route] ?? [], d.base.routes[route] ?? []),
  addresses: pendingWithIds(d, routeChangeIds(d, route)).map(
    ([, { route: r, code, address }]) => ({ route: r, code, address }),
  ),
})

export const discardRoute = (
  d: DailyRoutesDraft,
  route: string,
): DailyRoutesDraft => {
  const dropped = new Set(
    pendingWithIds(d, routeChangeIds(d, route)).map(([key]) => key),
  )
  // a dropped new code has no address left there, so that route may not keep
  // pointing at it; a code from a stored order stays, with or without one
  const orphaned = (name: string, code: string) =>
    dropped.has(stopKey(name, code)) &&
    storedAddress(d.base, name, code) === undefined &&
    !(d.base.routes[name] ?? []).includes(code)
  const orders = Object.fromEntries(
    Object.entries({
      ...d.orders,
      [route]: d.base.routes[route] ?? [],
    }).map(([name, codes]) => [name, codes.filter((c) => !orphaned(name, c))]),
  )
  return {
    ...d,
    orders,
    pending: Object.fromEntries(
      Object.entries(d.pending).filter(([key]) => !dropped.has(key)),
    ),
  }
}

export const afterSave = (
  d: DailyRoutesDraft,
  route: string,
  written: RoutesData,
): DailyRoutesDraft => {
  const saved = new Set(
    changesForRoute(d, route).addresses.map((a) => stopKey(a.route, a.code)),
  )
  const orders = copyRoutes(written.routes)
  for (const [other, codes] of Object.entries(d.orders)) {
    if (other === route) continue
    // only a reordered route keeps its edit; an address-only edit follows
    // what was written, or the stale order would revert someone else's change
    const reordered = !sameOrder(codes, d.base.routes[other] ?? [])
    if (reordered || !has(written.routes, other)) orders[other] = [...codes]
  }
  // an edit made while the save was in flight no longer matches what was
  // written, so it stays pending
  const isWritten = (key: string, entry: PendingAddress) =>
    saved.has(key) &&
    storedAddress(written, entry.route, entry.code) === entry.address
  return {
    ...d,
    base: written,
    orders,
    pending: Object.fromEntries(
      Object.entries(d.pending).filter(
        ([key, entry]) => !isWritten(key, entry),
      ),
    ),
  }
}
